import { FC, useState, useEffect, useCallback, use } from 'react';
import { BsArrowLeftShort, BsArrowRightShort } from 'react-icons/bs';
import {
	Container,
	Table,
	Row,
	Col,
	Button,
	Stack,
	Badge,
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import { QueryString } from 'nestjs-prisma-querybuilder-interface';
import {
	getEstablishmentById,
	getEstablishmentsService,
} from '@/services/api/establishments';

import {
	getEstablishments,
	FilterProps,
} from '@/services/querys/getEstablishments';
// Removendo import do tipo local Establishment para evitar conflito com SDK
import { DefaultValues } from '@/schemas/home';
import { publishEstablishment as publishEstablishmentService } from '@/services/api/establishments';
import Loading from '@/components/common/Loading';

import { getPlans as getPlansService } from '@/services/api/plans';

import { FormSearch } from './FormSearch';
import { statusTranslate } from '@/constants/statusTranslate';
import { canPublishEstablishment } from '@/utils/canPublishEstablishment';
import ModalInactiveEstablishment from '../Establishment/info/components/Card/ModalInactiveEstablishment';
import { useGlobalGurupassClient } from '@/hooks/useGurupassClient';

export const Home: FC = () => {
	const [dataEstablishments, setDataEstablishments] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [showModalInactiveEstablishment, setModalInactiveEstablishment] =
		useState(false);

	const [numberPage, setNumberPage] = useState<number>(1);
	const [filterEstablishments, setFilterEstablishments] = useState<
		FilterProps | undefined
	>();
	const [establishmentId, setEstablishmentId] = useState<string | null>(null);

	const { client, clientIsLoading } = useGlobalGurupassClient();

	const onGetFilteredEstablishments = useCallback(async () => {
		setLoading(true);
		try {
			const params = getEstablishments({
				page: numberPage,
				filter: filterEstablishments,
			});

			const response = await client?.admin
				.establishments()
				.filter(params)
				.run();
			const establishments = response?.data || [];

			setDataEstablishments(establishments);
		} finally {
			setLoading(false);
			setEstablishmentId(null);
		}
	}, [numberPage, filterEstablishments, client]);

	const getPlans = async () => await getPlansService(true);

	const getAllEstablishments = async () => await getEstablishmentsService(true);

	useEffect(() => {
		onGetFilteredEstablishments();
		getPlans();
		getAllEstablishments();
	}, [onGetFilteredEstablishments]);

	const onNextPage = () => {
		setNumberPage(numberPage + 1);
	};

	const onReturnPage = () => {
		setNumberPage(numberPage - 1);
	};

	const handleSearchEstablishments = (fields: DefaultValues) => {
		setNumberPage(1);
		setFilterEstablishments(fields as unknown as FilterProps);
	};

	const publishEstablishment = useCallback(
		async (establishmentId: string) => {
			try {
				const establishment = await getEstablishmentById(establishmentId);

				if (!establishment) {
					toast.error('Estabelecimento não encontrado!');
					return;
				}

				if (!canPublishEstablishment(establishment))
					throw new Error(
						'Estabelecimento não possui todos os campos obrigatórios preenchidos!'
					);
			} catch (error: any) {
				toast.error(error.message || 'Erro ao publicar estabelecimento!');
				return;
			}

			try {
				const response = await publishEstablishmentService(establishmentId);
				if (response) toast.info('Estabelecimento publicado com sucesso!');
				return response;
			} finally {
				onGetFilteredEstablishments();
			}
		},
		[onGetFilteredEstablishments]
	);

	console.log('dataEstablishments', dataEstablishments);

	return (
		<>
			{showModalInactiveEstablishment && (
				<ModalInactiveEstablishment
					handleShow={() =>
						setModalInactiveEstablishment(!showModalInactiveEstablishment)
					}
					establishmentId={`${establishmentId}`}
					getEstablishment={onGetFilteredEstablishments}
				/>
			)}
			<Container>
				<FormSearch onSubmitForm={handleSearchEstablishments} />
				{loading ? (
					<Row>
						<Col>
							<Stack className="d-flex justify-content-center align-items-center mt-5">
								<Loading />
							</Stack>
						</Col>
					</Row>
				) : (
					<>
						<Row>
							<Col>
								<Table striped className="mt-3">
									<thead>
										<tr>
											<th>Nome</th>
											<th>Bairro</th>
											<th>Cidade</th>
											<th>Estado</th>
											<th>Modalidade</th>
											<th>Status</th>
											<th>Ações</th>
										</tr>
									</thead>
									<tbody>
										{dataEstablishments?.map(establishment => {
											return (
												<tr key={`${establishment.id}_${establishment.name}`}>
													<td>
														<>
															{establishment.isFraudulent && (
																<span className="badge badge-dark mr-3">
																	Fraude
																</span>
															)}
															{establishment.name}
														</>
													</td>
													<td>{establishment?.neighborhood?.name}</td>
													<td>{establishment?.city?.name}</td>
													<td>{establishment?.city?.state?.name}</td>
													<td>
														{establishment.modalities?.map(
															({ modality }: any) => (
																<div key={`${modality.id}_${modality.name}`}>
																	<Badge bg="secondary">{modality.name}</Badge>
																</div>
															)
														)}
													</td>
													<td>
														{(statusTranslate as any)[establishment.status] ||
															establishment.status}
													</td>
													<td
														style={{
															display: 'flex',
															justifyContent: 'space-around',
														}}>
														<Button
															variant="primary"
															href={`/establishments/${establishment.id}`}>
															Visualizar
														</Button>
														{establishment.status !== 'PUBLISHED' ? (
															<Button
																variant="success"
																onClick={() =>
																	publishEstablishment(establishment.id)
																}>
																Publicar
															</Button>
														) : (
															<Button
																variant="warning"
																onClick={() => {
																	setEstablishmentId(establishment.id);
																	setModalInactiveEstablishment(true);
																}}>
																Inativar
															</Button>
														)}
													</td>
												</tr>
											);
										})}
										{dataEstablishments?.length <= 0 && (
											<tr>
												<td colSpan={6}>Nenhum registro encontrado</td>
											</tr>
										)}
									</tbody>
								</Table>
							</Col>
						</Row>
						<Row>
							<Col>
								<Stack direction="horizontal" gap={3} className="mx-auto mb-3">
									{numberPage > 1 && (
										<Button variant="primary" onClick={() => onReturnPage()}>
											<BsArrowLeftShort size={30} />
										</Button>
									)}
									{dataEstablishments.length > 0 && (
										<Button variant="primary" onClick={() => onNextPage()}>
											<BsArrowRightShort size={30} />
										</Button>
									)}
								</Stack>
							</Col>
						</Row>
					</>
				)}
			</Container>
		</>
	);
};

export default Home;
